/* =============================================================================
 * EmptyState — APS primitive (Track D)
 * One consistent empty surface (replaces ~40 hand-rolled "no results" blocks).
 * Motivating, not dead-ended: an icon, a title, one line of subcopy, and an
 * optional action. Distinct from ErrorState.tsx (which is for failures).
 * ========================================================================== */
import React from 'react';
import { radius, font, space } from '../../styles/tokens';

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  tone?: 'neutral' | 'success';
  style?: React.CSSProperties;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon, title, subtitle, action, tone = 'neutral', style,
}) => (
  <div
    role="status"
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: `${space[8]}px ${space[6]}px`,
      fontFamily: font.sans,
      ...style,
    }}
  >
    {icon && (
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          borderRadius: radius.pill,
          background: tone === 'success' ? 'var(--aps-success-tint)' : 'var(--aps-fill)',
          color: tone === 'success' ? 'var(--aps-success)' : 'var(--aps-text-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: space[4],
        }}
      >
        {icon}
      </div>
    )}
    <div style={{ fontSize: 17, fontWeight: font.weight.bold, color: 'var(--aps-ink)' }}>
      {title}
    </div>
    {subtitle && (
      <div style={{ fontSize: 14, color: 'var(--aps-text-2)', fontWeight: font.weight.medium, marginTop: 6, maxWidth: 320, lineHeight: 1.5 }}>
        {subtitle}
      </div>
    )}
    {action && <div style={{ marginTop: space[5] }}>{action}</div>}
  </div>
);

export default EmptyState;
